import { useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';

interface ToggleSwitchStyleProps {
	customToggleOnBg?: string;
	customToggleOffBg?: string;
	isDisabled?: boolean;
	customToggleOffKnobBg?: string;
	customToggleOnKnobBg?: string;
	customToggleDisabledKnobBg?: string;
	customToggleDisabledKnobOnTheLeft?: boolean;
}

const ToggleSwitchContainer = styled.div<ToggleSwitchStyleProps>`
	&.toggle-on {
		background: var(--cookie-toggle-bg-on, rgba(var(--base-07), 1));
		${({ customToggleOnBg }) =>
			customToggleOnBg &&
			css`
				background: ${customToggleOnBg};
			`};
	}
	&.toggle-off {
		background: var(--cookie-toggle-bg-off, rgba(var(--base-07), 1));
		${({ customToggleOffBg }) =>
			customToggleOffBg &&
			css`
				background: ${customToggleOffBg};
			`};
	}
	&.toggle-disabled {
		background: var(--cookie-toggle-disabled-bg, rgba(var(--base-07), 1));
	}
	height: 20px;
	width: 40px;
	border-radius: 1em;
	transition: background 0.2s ease-out;
	cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
	.knob {
		position: relative;
		width: 20px;
		height: 20px;
		background: var(--cookie-toggle-knob-off, rgba(var(--base-01), 1));
		border-radius: 50%;
		transform: translateX(0px);
		bottom: 0;
		transition: transform 0.2s ease-out;
		box-shadow: 2px 0px 3px rgba(0, 0, 0, 0.18);
		${({ customToggleOffKnobBg }) =>
			customToggleOffKnobBg &&
			css`
				background: ${customToggleOffKnobBg};
			`};
		&.active {
			box-shadow: -2px 0px 3px rgba(0, 0, 0, 0.18);
			background: var(--cookie-toggle-knob-on, rgba(var(--base-02), 1));
			transform: ${({ customToggleDisabledKnobOnTheLeft }) =>
				customToggleDisabledKnobOnTheLeft ? `translateX(0px)` : `translateX(20px)`};
			${({ customToggleOnKnobBg }) =>
				customToggleOnKnobBg &&
				css`
					background: ${customToggleOnKnobBg};
				`};
		}
	}
	&.toggle-disabled .knob {
		background: rgba(var(--base-07), 1);
		${({ customToggleDisabledKnobBg }) =>
			customToggleDisabledKnobBg &&
			css`
				background: ${customToggleDisabledKnobBg};
			`};
	}
`;

interface Props extends ToggleSwitchStyleProps {
	isActive: boolean;
	onClick: () => void;
	readonly?: boolean;
	toggleClass?: string;
	customToggleDisabledKnobOnTheLeft?: boolean;
	dataAt?: string;
}

function ToggleSwitch({
	isActive,
	onClick,
	toggleClass = '',
	readonly,
	customToggleOffBg,
	customToggleOnBg,
	customToggleOnKnobBg,
	customToggleOffKnobBg,
	customToggleDisabledKnobBg,
	isDisabled = false,
	customToggleDisabledKnobOnTheLeft,
	dataAt
}: Props) {
	const [isToggleOn, setToggle] = useState(isActive);

	useEffect(() => {
		setToggle(isActive);
	}, [isActive]);

	const onClickSwitch = useCallback(() => {
		if (isDisabled) return;
		if (!readonly) {
			setToggle(!isToggleOn);
		}
		onClick();
	}, [isDisabled, isToggleOn, readonly, onClick]);

	return (
		<ToggleSwitchContainer
			onClick={onClickSwitch}
			className={`${toggleClass} ${isToggleOn ? 'toggle-on' : 'toggle-off'} ${
				isDisabled ? 'toggle-disabled' : ''
			}`}
			customToggleOffBg={customToggleOffBg}
			customToggleOnBg={customToggleOnBg}
			customToggleOnKnobBg={customToggleOnKnobBg}
			customToggleOffKnobBg={customToggleOffKnobBg}
			customToggleDisabledKnobBg={customToggleDisabledKnobBg}
			isDisabled={isDisabled}
			customToggleDisabledKnobOnTheLeft={customToggleDisabledKnobOnTheLeft && isDisabled}
			data-at={dataAt}
		>
			<div className={isToggleOn ? 'knob active' : 'knob'} />
		</ToggleSwitchContainer>
	);
}

export default ToggleSwitch;
